/** Which chunk of which loop. Positional, like `Loop.id` — never persist it. */
export type ChunkRef = {
  readonly loopId: number;
  readonly chunkIndex: number;
};

/** Stable string key for a chunk, so chunks can live in a `Set` or a `Map`. */
export const chunkKey = ({ loopId, chunkIndex }: ChunkRef): string =>
  `${loopId}:${chunkIndex}`;

/**
 * The welded endpoint graph `stitchLoops` builds while assembling loops:
 * coincident endpoints merged into shared nodes, and the chunks that meet at
 * each node.
 *
 * Pure geometry, deliberately — it knows nothing about mapping roles or any
 * other reason two chunks should or should not chain. A caller walks the
 * graph itself and supplies that judgment per-walk, through `keyOf`, so a
 * chain can be recomputed against state the model has no notion of (roles,
 * which change under the user as they paint) without this graph changing.
 */
export type ChunkGraph = {
  /** The two welded nodes a chunk runs between, or `undefined` if it has none. */
  endsOf(
    chunk: ChunkRef,
  ): { readonly start: number; readonly end: number } | undefined;
  /** Every chunk with an endpoint welded at `node`. */
  chunksAt(node: number): readonly ChunkRef[];
};

/** An empty graph — for a drawing with nothing stitchable in it. */
export const EMPTY_CHUNK_GRAPH: ChunkGraph = {
  endsOf: () => undefined,
  chunksAt: () => [],
};

export type ChunkGraphInput = {
  /** Each chunk with the nodes it runs between. Self-closed chunks are omitted. */
  readonly ends: ReadonlyMap<string, { start: number; end: number }>;
  readonly byNode: ReadonlyMap<number, readonly ChunkRef[]>;
};

export const createChunkGraph = ({
  ends,
  byNode,
}: ChunkGraphInput): ChunkGraph => ({
  endsOf: (chunk) => ends.get(chunkKey(chunk)),
  chunksAt: (node) => byNode.get(node) ?? [],
});

export type WalkChainOptions = {
  /**
   * What decides whether two chunks belong to the same chain.
   *
   * Two chunks chain when their keys are equal, and `null` never chains with
   * anything — not even another `null`. That is what makes this the one hook the
   * whole rule hangs off: the DXF session passes an entity's mapping roles, and
   * `null` for a chunk on a hidden layer, so a chain can neither cross a role
   * boundary nor run through geometry the user has switched off.
   *
   * Deliberately opaque. This module knows nothing about roles, layers or
   * visibility — only about equality — so nothing here has to change when the
   * rule behind the key does.
   */
  keyOf: (chunk: ChunkRef) => string | null;
};

/**
 * The chain of chunks reachable from `seed` without ever guessing.
 *
 * Walks outward from both ends of the seed, and at each welded node takes the
 * one continuation whose key matches. The rule at a node is the whole design:
 *
 * - Exactly one matching continuation — step onto it. This is what lets a chain
 *   run *through* a T or K junction that the geometric stitcher had to stop at:
 *   the branch that carries a different role is no longer a candidate, so the
 *   junction stops being ambiguous and the profile continues.
 * - None — the chain ends, which is how a role boundary splits what was one
 *   geometric loop.
 * - Two or more — the chain ends as well. The continuation is genuinely
 *   ambiguous and the stitcher's own principle applies: guessing is how a walk
 *   silently swallows a feature into the wrong chain.
 *
 * Returns the seed alone when its key is `null`, so a hidden or unchainable
 * chunk still picks as itself rather than as nothing.
 */
export const walkChain = (
  graph: ChunkGraph,
  seed: ChunkRef,
  { keyOf }: WalkChainOptions,
): readonly ChunkRef[] => {
  const key = keyOf(seed);
  if (key === null) {
    return [seed];
  }

  const visited = new Set<string>([chunkKey(seed)]);

  /**
   * The single matching chunk at `node` that is not `from`, or `null`.
   *
   * Ambiguity is judged over *every* matching branch, visited or not, and only
   * then is an already-visited winner refused. Filtering the visited out first
   * looks equivalent and is not: the two runs share one `visited` set, so a
   * junction the backward run correctly refused could be walked straight through
   * by the forward run once the backward run had consumed one of the rivals.
   * Counting first keeps a junction ambiguous no matter which side reaches it.
   *
   * Refusing a visited winner is what ends a closed chain: the walk comes back
   * around to its own seed, finds it unambiguously, and stops rather than
   * looping forever.
   */
  const continuationAt = (node: number, from: ChunkRef): ChunkRef | null => {
    const fromKey = chunkKey(from);
    const candidates = graph
      .chunksAt(node)
      .filter((chunk) => chunkKey(chunk) !== fromKey && keyOf(chunk) === key);
    if (candidates.length !== 1) {
      return null;
    }
    const [continuation] = candidates;
    if (!continuation) {
      return null;
    }
    return visited.has(chunkKey(continuation)) ? null : continuation;
  };

  /** Walks one direction, returning the chunks found in the order visited. */
  const runFrom = (startNode: number): ChunkRef[] => {
    const found: ChunkRef[] = [];
    let node = startNode;
    let from = seed;

    for (;;) {
      const next = continuationAt(node, from);
      if (!next) {
        return found;
      }
      visited.add(chunkKey(next));
      found.push(next);

      const ends = graph.endsOf(next);
      if (!ends) {
        return found;
      }
      // Arriving at the chunk's start means leaving by its end, and vice versa.
      node = ends.start === node ? ends.end : ends.start;
      from = next;
    }
  };

  const ends = graph.endsOf(seed);
  if (!ends) {
    return [seed];
  }

  // Backward run reversed and placed ahead of the seed, so the result reads
  // along the chain rather than outward from the middle of it.
  return [...runFrom(ends.start).reverse(), seed, ...runFrom(ends.end)];
};
